import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataGrid } from '../../components/DataGrid.js';
import {
  employeeLabelOf,
  employeeStatusOf,
  employeeTerritoryOf,
  listEmployees,
  type EmployeeRow,
} from '../../api/orgApi.js';

export function EmployeeListPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<EmployeeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listEmployees(token)
      .then(r => setRows(r.data))
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <main aria-label="employee-list" style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1>{t('org.employee.list.title')}</h1>
        <Link to="/org/employees/new" style={{ padding: '8px 16px', borderRadius: 6, background: 'var(--color-brand-primary)', color: 'var(--color-text-inverse)', textDecoration: 'none' }}>{t('org.employee.list.new')}</Link>
      </div>
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {loading && <p>{t('common.loading')}</p>}
      {!loading && rows.length === 0 && <p>{t('org.employee.list.empty')}</p>}
      {rows.length > 0 && (
        <DataGrid<EmployeeRow>
          caption="employees"
          rows={rows}
          getRowId={(e) => e.empExtId}
          rowTestId={(e) => `emp-${e.empExtId}`}
          searchPlaceholder={t('org.employee.list.searchPh')}
          columns={[
            {
              key: 'code',
              header: t('org.col.code'),
              value: (e) => e.employeeCode,
              render: (e) => (
                <Link to={`/org/employees/${e.empExtId}`} style={{ color: 'var(--color-text-link)' }}>{e.employeeCode}</Link>
              ),
            },
            { key: 'name', header: t('org.col.name'), value: (e) => employeeLabelOf(e) },
            { key: 'department', header: t('org.col.department'), value: (e) => e.departmentName ?? '—' },
            { key: 'designation', header: t('org.col.designation'), value: (e) => e.designationName ?? '—' },
            { key: 'territory', header: t('org.col.territory'), value: (e) => employeeTerritoryOf(e) ?? '—' },
            {
              key: 'status',
              header: t('org.col.status'),
              value: (e) => employeeStatusOf(e),
              render: (e) => (
                <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 12, background: employeeStatusOf(e) === 'ACTIVE' ? 'var(--color-success-bg)' : 'var(--color-danger-bg)' }}>{employeeStatusOf(e)}</span>
              ),
            },
          ]}
        />
      )}
    </main>
  );
}
